import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import LazyImage from "../ui/LazyImage";
import { Download, Plus, Info, RefreshCw, Trash2, Inbox, X, FileCheck2, Upload, CloudUpload, ImageIcon } from "lucide-react";
import { getAllLocales, createLocale, deleteLocale, updateLocale } from "./fetchApi";
import i18n from "../../i18n";

const EN_KEYS = Object.entries(i18n.getResourceBundle("EN", "translation") || {});

const Locales = ({ colors }) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [updateTarget, setUpdateTarget] = useState(null); // locale object being updated
    const [locales, setLocales] = useState([]);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        name: '', displayName: '', code: '', displayCode: '', csvFile: null, iconFile: null
    });
    const [iconPreview, setIconPreview] = useState(null);

    const [updateCsvFile, setUpdateCsvFile] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);

    const iconInputRef = useRef(null);
    const csvInputRef = useRef(null);
    const updateCsvRef = useRef(null);

    const fetchLocalesData = async (isMountedRef) => {
        let res = await getAllLocales();
        if ((!isMountedRef || isMountedRef.current) && res && res.success) {
            setLocales(res.locales);
        }
    };

    useEffect(() => {
        const isMountedRef = { current: true };
        fetchLocalesData(isMountedRef);
        return () => { isMountedRef.current = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Export CSV template ──────────────────────────────────────────────────
    const handleExport = () => {
        const rows = [["key", "value"], ...EN_KEYS];
        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "locale_EN_template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── Add locale ───────────────────────────────────────────────────────────
    const handleCloseAdd = () => {
        setIsAddModalOpen(false);
        setFormData({ name: '', displayName: '', code: '', displayCode: '', csvFile: null, iconFile: null });
        setIconPreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.code || !formData.csvFile || !formData.iconFile)
            return toast.error("Please fill in all required fields (*)");

        setLoading(true);
        const data = new FormData();
        data.append("name", formData.name);
        data.append("displayName", formData.displayName);
        data.append("code", formData.code);
        data.append("displayCode", formData.displayCode);
        data.append("iconFile", formData.iconFile);
        data.append("csvFile", formData.csvFile);

        let res = await createLocale(data);
        if (res && res.success) {
            toast.success("Locale added successfully!");
            handleCloseAdd();
            fetchLocalesData();
        } else {
            toast.error(res.error || "An error occurred!");
        }
        setLoading(false);
    };

    // ── Update locale ────────────────────────────────────────────────────────
    const handleOpenUpdate = (item) => {
        setUpdateTarget(item);
        setUpdateCsvFile(null);
    };

    const handleCloseUpdate = () => {
        setUpdateTarget(null);
        setUpdateCsvFile(null);
    };

    const handleSubmitUpdate = async (e) => {
        e.preventDefault();
        if (!updateCsvFile) return toast.error("Please select a CSV file to upload");

        setUpdateLoading(true);
        const data = new FormData();
        data.append("csvFile", updateCsvFile);

        let res = await updateLocale(updateTarget._id, data);
        if (res && res.success) {
            toast.success("Locale updated successfully!");
            handleCloseUpdate();
            fetchLocalesData();
        } else {
            toast.error(res.error || "An error occurred!");
        }
        setUpdateLoading(false);
    };

    // ── Delete ───────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this locale?")) {
            let res = await deleteLocale(id);
            if (res && res.success) {
                toast.success("Deleted successfully!");
                fetchLocalesData();
            } else {
                toast.error(res.error || "Failed to delete!");
            }
        }
    };

    const cardStyle = {
        backgroundColor: colors.bgCard,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
    };

    const inputStyle = {
        backgroundColor: colors.bgApp,
        color: colors.textMain,
        borderRadius: '10px',
        border: `1px solid ${colors.borderColor}`,
        padding: '10px 15px'
    };

    const overlayStyle = {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 99999,
        backdropFilter: 'blur(5px)',
        display: 'flex', justifyContent: 'center', alignItems: 'center'
    };

    const modalBoxStyle = {
        width: '460px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto',
        backgroundColor: colors.bgCard, borderRadius: '24px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)', padding: '28px'
    };

    return (
        <div className="animate-fade-in position-relative">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold mb-1" style={{ color: colors.textMain, letterSpacing: '0.5px' }}>Locales</h3>
                    <span style={{ color: colors.textMuted, fontSize: '14px' }}>Manage translation files for the storefront</span>
                </div>
                <div className="d-flex gap-3">
                    <button onClick={handleExport} className="btn fw-bold px-4 shadow-sm" style={{ backgroundColor: '#5B5CE6', color: '#FFF', borderRadius: '10px' }}>
                        <Download size={15} className="me-2" />Export original language
                    </button>
                    <button onClick={() => setIsAddModalOpen(true)} className="btn fw-bold px-4 shadow-sm" style={{ border: `1px solid ${colors.textMain}`, backgroundColor: 'transparent', color: colors.textMain, borderRadius: '10px' }}>
                        <Plus size={15} className="me-2" />Add locales
                    </button>
                </div>
            </div>

            {/* CSV format hint */}
            <div className="mb-3 px-1" style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                <Info size={14} className="me-1" />
                CSV format: two columns — <code>key</code> and <code>value</code>. Use "Export original language" to download the template.
            </div>

            <div className="p-0 shadow-sm overflow-hidden" style={cardStyle}>
                <table className="table table-borderless align-middle mb-0" style={{ color: colors.textMain }}>
                    <thead style={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                        <tr className="small text-uppercase fw-bold text-muted" style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                            <th className="py-3 ps-4">NO.</th>
                            <th className="py-3">NAME</th>
                            <th className="py-3">CODE</th>
                            <th className="py-3 text-center">IMAGE</th>
                            <th className="py-3">ACTION</th>
                        </tr>
                    </thead>
                    <tbody>
                        {locales.length > 0 ? locales.map((item, index) => (
                            <tr key={item._id} style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                                <td className="py-3 ps-4">{index + 1}</td>
                                <td className="py-3 fw-bold">{item.name}</td>
                                <td className="py-3">
                                    <span className="badge fw-bold px-3 py-2" style={{ backgroundColor: 'rgba(91,92,230,0.1)', color: '#5B5CE6', borderRadius: '8px' }}>
                                        {item.code}
                                    </span>
                                </td>
                                <td className="py-3 text-center">
                                    <LazyImage
                                        src={item.iconFile}
                                        alt={item.name}
                                        style={{ width: '45px', height: '35px', objectFit: 'contain', border: `1px solid ${colors.borderColor}`, borderRadius: '6px', padding: '2px', backgroundColor: colors.bgApp }}
                                    />
                                </td>
                                <td className="py-3">
                                    <button
                                        onClick={() => handleOpenUpdate(item)}
                                        className="btn btn-sm me-2 fw-bold"
                                        style={{ borderRadius: '8px', border: `1px solid ${colors.borderColor}`, color: colors.textMain }}
                                    >
                                        <RefreshCw size={13} className="me-1" />Update locales
                                    </button>
                                    <button
                                        onClick={() => handleDelete(item._id)}
                                        className="btn btn-sm text-white fw-bold"
                                        style={{ backgroundColor: '#D9493F', borderRadius: '8px' }}
                                    >
                                        <Trash2 size={13} className="me-1" />Delete
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="5" className="text-center py-5 text-muted"><Inbox size={32} style={{ display: 'block', margin: '0 auto 8px' }} />No locales available</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── ADD MODAL ─────────────────────────────────────────────────── */}
            {isAddModalOpen && (
                <div style={overlayStyle} onClick={handleCloseAdd}>
                    <div style={modalBoxStyle} onClick={e => e.stopPropagation()}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 className="fw-bold m-0" style={{ color: colors.textMain }}>Add Locales</h4>
                            <button onClick={handleCloseAdd} style={{ background: 'none', border: `1px solid ${colors.borderColor}`, color: colors.textMain, borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <X size={16} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label fw-bold small mb-1" style={{ color: colors.textMuted }}>Name <span className="text-danger">*</span></label>
                                <input type="text" className="form-control" style={inputStyle} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold small mb-1" style={{ color: colors.textMuted }}>Display name</label>
                                <input type="text" className="form-control" style={inputStyle} value={formData.displayName} onChange={e => setFormData({ ...formData, displayName: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold small mb-1" style={{ color: colors.textMuted }}>Code (e.g. VN) <span className="text-danger">*</span></label>
                                <input type="text" className="form-control" style={inputStyle} value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })} required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold small mb-1" style={{ color: colors.textMuted }}>Display code</label>
                                <input type="text" className="form-control" style={inputStyle} value={formData.displayCode} onChange={e => setFormData({ ...formData, displayCode: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold small mb-1" style={{ color: colors.textMuted }}>Translate file (CSV) <span className="text-danger">*</span></label>
                                <div className="d-flex justify-content-center align-items-center py-4" style={{ backgroundColor: colors.bgApp, borderRadius: '12px', border: `1px dashed ${colors.borderColor}` }}>
                                    <input type="file" accept=".csv" ref={csvInputRef} onChange={e => setFormData({ ...formData, csvFile: e.target.files[0] })} className="d-none" />
                                    <button type="button" onClick={() => csvInputRef.current.click()} className="btn btn-sm fw-bold d-inline-flex align-items-center px-4 py-2" style={{ backgroundColor: colors.bgCard, color: colors.textMain, border: `1px solid ${colors.borderColor}`, borderRadius: '8px' }}>
                                        {formData.csvFile ? <><FileCheck2 size={15} className="text-success me-2" />{formData.csvFile.name}</> : <><Upload size={15} className="me-2" />Upload CSV file</>}
                                    </button>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="form-label fw-bold small mb-1" style={{ color: colors.textMuted }}>Display icon (Image) <span className="text-danger">*</span></label>
                                <div
                                    className="d-flex justify-content-center align-items-center"
                                    style={{ width: '90px', height: '90px', backgroundColor: colors.bgApp, border: `1px solid ${colors.borderColor}`, borderRadius: '12px', cursor: 'pointer', overflow: 'hidden' }}
                                    onClick={() => iconInputRef.current.click()}
                                >
                                    <input type="file" accept="image/*" ref={iconInputRef} onChange={e => { const f = e.target.files[0]; if (f) { setFormData({ ...formData, iconFile: f }); setIconPreview(URL.createObjectURL(f)); } }} className="d-none" />
                                    {iconPreview
                                        ? <LazyImage src={iconPreview} alt="Icon Preview" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} />
                                        : <div className="text-center" style={{ color: colors.textMuted }}><ImageIcon size={24} /></div>
                                    }
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-3 pt-4" style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                                <button type="button" onClick={handleCloseAdd} className="btn fw-bold px-4 py-2" style={{ backgroundColor: 'transparent', color: colors.textMain, border: `1px solid ${colors.borderColor}`, borderRadius: '10px' }}>Cancel</button>
                                <button type="submit" disabled={loading} className="btn text-white fw-bold px-4 py-2" style={{ backgroundColor: '#988CF8', borderRadius: '10px' }}>
                                    {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── UPDATE MODAL ──────────────────────────────────────────────── */}
            {updateTarget && (
                <div style={overlayStyle} onClick={handleCloseUpdate}>
                    <div style={{ ...modalBoxStyle, width: '420px' }} onClick={e => e.stopPropagation()}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h4 className="fw-bold m-0" style={{ color: colors.textMain }}>Update Locale</h4>
                                <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                                    {updateTarget.name} ({updateTarget.code})
                                </span>
                            </div>
                            <button onClick={handleCloseUpdate} style={{ background: 'none', border: `1px solid ${colors.borderColor}`, color: colors.textMain, borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <X size={16} />
                            </button>
                        </div>

                        <div className="mb-3 p-3" style={{ backgroundColor: 'rgba(91,92,230,0.06)', borderRadius: '10px', border: '1px solid rgba(91,92,230,0.15)' }}>
                            <p className="m-0 small" style={{ color: colors.textMuted }}>
                                <Info size={14} className="me-1 text-primary" />
                                Upload a new CSV file to replace the current translations. Use "Export original language" to get the key template.
                            </p>
                        </div>

                        <form onSubmit={handleSubmitUpdate}>
                            <div className="mb-4">
                                <label className="form-label fw-bold small mb-2" style={{ color: colors.textMuted }}>New translate file (CSV) <span className="text-danger">*</span></label>
                                <div
                                    className="d-flex flex-column justify-content-center align-items-center py-5"
                                    style={{ backgroundColor: colors.bgApp, borderRadius: '12px', border: `2px dashed ${updateCsvFile ? '#22c55e' : colors.borderColor}`, cursor: 'pointer', transition: 'border-color 0.2s' }}
                                    onClick={() => updateCsvRef.current.click()}
                                >
                                    <input type="file" accept=".csv" ref={updateCsvRef} onChange={e => setUpdateCsvFile(e.target.files[0])} className="d-none" />
                                    {updateCsvFile ? (
                                        <>
                                            <FileCheck2 size={32} className="text-success mb-2" />
                                            <span className="fw-bold" style={{ color: '#22c55e' }}>{updateCsvFile.name}</span>
                                            <span className="small text-muted mt-1">Click to change file</span>
                                        </>
                                    ) : (
                                        <>
                                            <CloudUpload size={32} className="mb-2" style={{ color: colors.textMuted }} />
                                            <span className="fw-bold" style={{ color: colors.textMain }}>Click to upload CSV</span>
                                            <span className="small text-muted mt-1">Replaces current translations</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-3 pt-4" style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                                <button type="button" onClick={handleCloseUpdate} className="btn fw-bold px-4 py-2" style={{ backgroundColor: 'transparent', color: colors.textMain, border: `1px solid ${colors.borderColor}`, borderRadius: '10px' }}>Cancel</button>
                                <button type="submit" disabled={updateLoading} className="btn text-white fw-bold px-4 py-2" style={{ backgroundColor: '#22c55e', borderRadius: '10px' }}>
                                    {updateLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Updating...</> : <><RefreshCw size={14} className="me-2" />Update</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Locales;
